import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Maximize2, X, Search, MessageCircle, FileText, Zap, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";

const FONT_OPTIONS = [
  { label: "Sans-serif", value: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif" },
  { label: "Serif", value: "Georgia, 'Times New Roman', Times, serif" },
  { label: "Monospace", value: "'SF Mono', SFMono-Regular, Menlo, Consolas, 'Courier New', monospace" },
  { label: "Rounded", value: "system-ui, 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" },
];

interface BannerData {
  body: string;
  cta_text: string;
  cta_link: string;
  font: string;
  bg_color: string;
  text_color: string;
  btn_color: string;
  btn_text_color: string;
  btn_border_radius: number;
  padding_x: number;
}

const DEMO_DEFAULTS: BannerData = {
  body: "We are investigating user reports of the app crashing.",
  cta_text: "View status",
  cta_link: "#",
  font: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  bg_color: "#fef2f2",
  text_color: "#991b1b",
  btn_color: "#dc2626",
  btn_text_color: "#ffffff",
  btn_border_radius: 6,
  padding_x: 16,
};

function BannerStrip({ data }: { data: BannerData }) {
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-2 py-2.5 shrink-0"
      style={{
        backgroundColor: data.bg_color,
        fontFamily: data.font,
        paddingLeft: `${data.padding_x}px`,
        paddingRight: `${data.padding_x}px`,
      }}
    >
      <span className="text-sm leading-snug" style={{ color: data.text_color }}>
        {data.body || <span className="opacity-40">Your banner message…</span>}
      </span>
      {data.cta_text && (
        <a
          href="#"
          onClick={(e) => e.preventDefault()}
          className="inline-flex shrink-0 items-center px-3 py-1 text-xs font-medium transition-opacity hover:opacity-90"
          style={{
            backgroundColor: data.btn_color,
            color: data.btn_text_color,
            borderRadius: `${data.btn_border_radius}px`,
          }}
        >
          {data.cta_text}
        </a>
      )}
    </div>
  );
}

function FakeSupportPage({ data }: { data: BannerData }) {
  const categories = [
    { icon: MessageCircle, label: "Contact us", desc: "Open a ticket or chat with support" },
    { icon: FileText, label: "Documentation", desc: "Guides, API docs, and references" },
    { icon: Zap, label: "System status", desc: "Uptime and incident history" },
    { icon: BookOpen, label: "FAQs", desc: "Answers to common questions" },
  ];

  return (
    <div className="flex flex-col h-full bg-white text-gray-900 overflow-hidden">
      {/* Banner */}
      <BannerStrip data={data} />

      {/* Site nav */}
      <div className="border-b border-gray-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-indigo-600 flex items-center justify-center">
            <span className="text-white text-xs font-bold">F</span>
          </div>
          <span className="font-semibold text-sm text-gray-800">FYI Co</span>
        </div>
        <nav className="flex items-center gap-4 text-xs text-gray-500">
          <span className="cursor-default">Home</span>
          <span className="cursor-default font-medium text-indigo-600 underline underline-offset-4 decoration-indigo-600">Support</span>
          <span className="cursor-default">Docs</span>
        </nav>
      </div>

      {/* Hero */}
      <div className="px-6 pt-8 pb-6 bg-gray-50 border-b border-gray-200 text-center">
        <h1 className="text-xl font-bold text-gray-900 mb-1">How can we help?</h1>
        <p className="text-sm text-gray-500 mb-4">Search our help center or browse by topic.</p>
        <div className="relative max-w-md mx-auto text-left">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <div className="w-full pl-9 pr-4 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-400">
            Search for articles…
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="flex-1 overflow-y-auto p-6">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Browse topics</h2>
        <div className="grid grid-cols-2 gap-3">
          {categories.map((cat) => (
            <div
              key={cat.label}
              className="rounded-lg border border-gray-200 p-4 hover:border-indigo-300 hover:bg-indigo-50/40 transition-colors cursor-default"
            >
              <cat.icon className="w-5 h-5 text-indigo-500 mb-2" />
              <div className="font-medium text-sm text-gray-800">{cat.label}</div>
              <div className="text-xs text-gray-500 mt-0.5">{cat.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 w-8 cursor-pointer rounded border border-border bg-transparent p-0.5 shrink-0"
        />
        <Input value={value} onChange={(e) => onChange(e.target.value)} className="font-mono text-xs h-8" />
      </div>
    </div>
  );
}

function BrowserChrome({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col h-full rounded-lg overflow-hidden border border-border shadow-lg">
      {/* Chrome bar */}
      <div className="bg-[#2a2a2a] px-3 py-2 flex items-center gap-3 shrink-0">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
          <div className="w-3 h-3 rounded-full bg-[#febc2e]" />
          <div className="w-3 h-3 rounded-full bg-[#28c840]" />
        </div>
        <div className="flex-1 bg-[#1a1a1a] rounded text-xs text-gray-400 px-3 py-1 text-center max-w-xs mx-auto font-mono">
          fyico.com/support
        </div>
      </div>
      <div className="flex-1 overflow-hidden">{children}</div>
    </div>
  );
}

// Thumbnail shown in the landing page hero
function DemoThumbnail({ data, onExpand }: { data: BannerData; onExpand: () => void }) {
  return (
    <div
      className="relative w-full max-w-2xl mx-auto sm:cursor-pointer group"
      onClick={onExpand}
    >
      <div className="rounded-xl overflow-hidden border border-border shadow-2xl bg-card">
        {/* Browser chrome */}
        <div className="bg-[#2a2a2a] px-3 py-2 flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
          </div>
          <div className="flex-1 bg-[#1a1a1a] rounded text-[10px] text-gray-400 px-3 py-0.5 text-center max-w-xs mx-auto font-mono">
            fyico.com/support
          </div>
        </div>

        {/* Fake page preview (scaled down) */}
        <div className="bg-white">
          {/* Banner */}
          <div
            className="flex items-center justify-between gap-3 py-2"
            style={{
              backgroundColor: data.bg_color,
              fontFamily: data.font,
              paddingLeft: `${data.padding_x}px`,
              paddingRight: `${data.padding_x}px`,
            }}
          >
            <span className="text-xs leading-snug" style={{ color: data.text_color }}>
              {data.body}
            </span>
            {data.cta_text && (
              <span
                className="shrink-0 text-[10px] font-medium px-2.5 py-0.5"
                style={{
                  backgroundColor: data.btn_color,
                  color: data.btn_text_color,
                  borderRadius: `${data.btn_border_radius}px`,
                }}
              >
                {data.cta_text}
              </span>
            )}
          </div>

          {/* Nav */}
          <div className="border-b border-gray-200 px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 rounded bg-indigo-600 flex items-center justify-center">
                <span className="text-white text-[8px] font-bold">F</span>
              </div>
              <span className="font-semibold text-xs text-gray-800">FYI Co</span>
            </div>
            <div className="flex items-center gap-3 text-[10px] text-gray-400">
              <span>Home</span>
              <span className="text-indigo-600 font-medium underline underline-offset-2 decoration-indigo-600">Support</span>
              <span>Docs</span>
            </div>
          </div>

          {/* Hero search */}
          <div className="px-4 py-4 bg-gray-50 border-b border-gray-100 text-center">
            <div className="text-sm font-bold text-gray-800 mb-1">How can we help?</div>
            <div className="rounded border border-gray-200 bg-white text-[10px] text-gray-300 px-3 py-1.5 max-w-xs mx-auto text-left">
              Search for articles…
            </div>
          </div>

          {/* Cards row */}
          <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
            {["Contact us", "Docs", "Status", "FAQs"].map((label) => (
              <div
                key={label}
                className="rounded border border-gray-200 p-2 text-center"
              >
                <div className="w-3 h-3 rounded-full bg-indigo-100 mx-auto mb-1" />
                <div className="text-[9px] text-gray-600 font-medium">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Overlay with expand hint — desktop only */}
      <div className="hidden sm:flex absolute inset-0 rounded-xl bg-black/0 group-hover:bg-black/20 transition-colors items-center justify-center">
        <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-background/90 backdrop-blur-sm border border-border rounded-lg px-4 py-2.5 flex items-center gap-2 text-sm font-medium shadow-lg">
          <Maximize2 className="w-4 h-4" />
          Try the editor
        </div>
      </div>

      {/* Expand icon always visible — desktop only */}
      <button
        className="hidden sm:block absolute bottom-3 right-3 bg-background/80 backdrop-blur-sm border border-border rounded p-1.5 text-muted-foreground hover:text-foreground transition-colors"
        onClick={(e) => { e.stopPropagation(); onExpand(); }}
        aria-label="Expand demo"
      >
        <Maximize2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// Full-screen demo modal
function DemoModal({ data, onChange, onClose }: { data: BannerData; onChange: (d: BannerData) => void; onClose: () => void }) {
  const update = (field: keyof BannerData, value: string | number) => {
    onChange({ ...data, [field]: value });
  };

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="w-full max-w-6xl h-[95vh] sm:h-[90vh] flex flex-col bg-background border border-border rounded-xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Modal header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
          <div className="text-sm font-medium">
            Try the banner editor
            <span className="ml-2 text-xs text-muted-foreground font-normal hidden sm:inline">No sign-up required</span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close demo"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body — stacked on mobile, side-by-side on sm+ */}
        <div className="flex-1 flex flex-col sm:flex-row min-h-0">
          {/* Fake support page */}
          <div className="flex-1 p-3 sm:p-4 min-w-0 min-h-0 hidden sm:block">
            <BrowserChrome>
              <FakeSupportPage data={data} />
            </BrowserChrome>
          </div>

          {/* On mobile: inline banner preview above editor */}
          <div className="sm:hidden shrink-0 border-b border-border">
            <BannerStrip data={data} />
          </div>

          {/* Editor panel */}
          <div className="sm:w-80 shrink-0 sm:border-l border-border flex flex-col overflow-hidden flex-1 sm:flex-none">
            <div className="px-4 py-3 border-b border-border shrink-0 hidden sm:block">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Banner editor</div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-5">
              {/* Content */}
              <section className="space-y-3">
                <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Content</h3>
                <div className="space-y-1.5">
                  <Label className="text-xs">Message</Label>
                  <Textarea
                    value={data.body}
                    onChange={(e) => update("body", e.target.value)}
                    placeholder="We just launched something exciting…"
                    rows={2}
                    className="text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Button text</Label>
                    <Input value={data.cta_text} onChange={(e) => update("cta_text", e.target.value)} placeholder="Learn more" className="h-8 text-sm" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Button link</Label>
                    <Input value={data.cta_link} onChange={(e) => update("cta_link", e.target.value)} placeholder="https://…" className="h-8 text-sm" />
                  </div>
                </div>
              </section>

              {/* Style */}
              <section className="space-y-3">
                <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Style</h3>
                <div className="space-y-1.5">
                  <Label className="text-xs">Font</Label>
                  <Select value={data.font} onValueChange={(v) => update("font", v)}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FONT_OPTIONS.map((f) => (
                        <SelectItem key={f.label} value={f.value}>
                          <span style={{ fontFamily: f.value }}>{f.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <ColorField label="Background" value={data.bg_color} onChange={(v) => update("bg_color", v)} />
                  <ColorField label="Text" value={data.text_color} onChange={(v) => update("text_color", v)} />
                  <ColorField label="Button" value={data.btn_color} onChange={(v) => update("btn_color", v)} />
                  <ColorField label="Button text" value={data.btn_text_color} onChange={(v) => update("btn_text_color", v)} />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">Button radius: {data.btn_border_radius}px</Label>
                  <Slider
                    value={[data.btn_border_radius]}
                    onValueChange={([v]) => update("btn_border_radius", v)}
                    min={0} max={24} step={1}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">Padding: {data.padding_x}px</Label>
                  <Slider
                    value={[data.padding_x]}
                    onValueChange={([v]) => update("padding_x", v)}
                    min={0} max={64} step={4}
                  />
                </div>
              </section>
            </div>

            {/* CTA */}
            <div className="p-4 border-t border-border shrink-0 space-y-2">
              <Link to="/signup" onClick={onClose}>
                <Button className="w-full font-semibold">Sign up to save & publish →</Button>
              </Link>
              <p className="text-[11px] text-muted-foreground text-center">Free to start. No credit card required.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function DemoPreview() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<BannerData>(DEMO_DEFAULTS);
  const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches;

  return (
    <>
      <DemoThumbnail data={data} onExpand={() => { if (!isMobile) setOpen(true); }} />
      {open && (
        <DemoModal data={data} onChange={setData} onClose={() => setOpen(false)} />
      )}
    </>
  );
}
